import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Eye,
  EyeOff,
  Power,
  PowerOff,
  Copy,
  Calendar,
  Server,
  ExternalLink,
} from 'lucide-react';
import { API_URL } from '../globals';
import { supabase } from '../supabaseClient';
import axios from 'axios';

const APIKeysTab = ({ user }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRevealing, setIsRevealing] = useState({});
  const [revealedKeys, setRevealedKeys] = useState({});
  const [isDeactivating, setIsDeactivating] = useState({});
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedKey, setSelectedKey] = useState(null);
  const [revealDialogOpen, setRevealDialogOpen] = useState(false);

  // Fetch team API keys
  const fetchTeamAPIKeys = async () => {
    if (!user?.team?.id) return;

    setIsLoading(true);
    setError('');
    
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/api_keys/team/api_keys`, {
        headers: headers,
      });

      if (response.data) {
        setApiKeys(response.data.api_keys || []);
      }
    } catch (error) {
      console.error('Error fetching team API keys:', error);
      setError(error.response?.data?.error || 'Failed to fetch API keys');
    } finally {
      setIsLoading(false);
    }
  };

  // Reveal full API key
  const handleRevealKey = async (keyId) => {
    setIsRevealing(prev => ({ ...prev, [keyId]: true }));
    
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/api_keys/api_key/${keyId}/reveal`, {
        headers: headers,
      });

      if (response.data) {
        setRevealedKeys(prev => ({
          ...prev,
          [keyId]: response.data.api_key_value
        }));
      }
    } catch (error) {
      console.error('Error revealing API key:', error);
      setError(error.response?.data?.error || 'Failed to reveal API key');
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsRevealing(prev => ({ ...prev, [keyId]: false }));
    }
  };

  // Hide revealed key
  const handleHideKey = (keyId) => {
    setRevealedKeys(prev => {
      const updated = { ...prev };
      delete updated[keyId];
      return updated;
    });
  };

  // Deactivate API key
  const handleDeactivateKey = async (keyId, keyName) => {
    const confirmDeactivate = window.confirm(
      `Are you sure you want to deactivate the API key "${keyName}"? This will stop all applications using this key.`
    );
    
    if (!confirmDeactivate) return;

    setIsDeactivating(prev => ({ ...prev, [keyId]: true }));
    
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.post(`${API_URL}/api_keys/api_key/${keyId}/deactivate`, {}, {
        headers: headers,
      });

      if (response.status === 200) {
        setSuccessMessage('API key deactivated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        // Refresh the API keys list
        fetchTeamAPIKeys();
      }
    } catch (error) {
      console.error('Error deactivating API key:', error);
      setError(error.response?.data?.error || 'Failed to deactivate API key');
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsDeactivating(prev => ({ ...prev, [keyId]: false }));
    }
  };

  // Copy to clipboard
  const handleCopyKey = async (keyValue) => {
    try {
      await navigator.clipboard.writeText(keyValue);
      setSuccessMessage('API key copied to clipboard');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setError('Failed to copy to clipboard');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    fetchTeamAPIKeys();
  }, [user]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} sx={{ color: '#fff' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          color: '#fff',
          fontWeight: 600,
          mb: 1,
        }}
      >
        API Keys
      </Typography>
      <Typography
        sx={{
          color: '#888',
          mb: 4,
          lineHeight: 1.6,
        }}
      >
        Manage API keys for all projects in your team. Keep your keys secure and deactivate any compromised keys immediately.
      </Typography>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            backgroundColor: '#2d1b1b',
            color: '#ff6b6b',
            '& .MuiAlert-icon': { color: '#ff6b6b' },
          }}
        >
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            backgroundColor: '#1b2d1b',
            color: '#51cf66',
            '& .MuiAlert-icon': { color: '#51cf66' },
          }}
        >
          {successMessage}
        </Alert>
      )}

      {apiKeys.length === 0 ? (
        <Card sx={{ 
          backgroundColor: '#1a1a1a', 
          border: '1px solid #2a2a2a',
          textAlign: 'center',
          py: 4,
        }}>
          <CardContent>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              mb: 2 
            }}>
              <Server size={48} color="#666" />
            </Box>
            <Typography sx={{ color: '#fff', mb: 1, fontSize: '1.1rem' }}>
              No API Keys Found
            </Typography>
            <Typography sx={{ color: '#888' }}>
              Your team doesn't have any API keys yet. API keys are automatically created when you set up Playwright projects.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ borderBottom: '1px solid #2a2a2a' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 600, borderBottom: 'none' }}>
                  Key Name
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 600, borderBottom: 'none' }}>
                  Project
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 600, borderBottom: 'none' }}>
                  API Key
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 600, borderBottom: 'none' }}>
                  Status
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 600, borderBottom: 'none' }}>
                  Last Used
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 600, borderBottom: 'none' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((apiKey) => (
                <TableRow key={apiKey.id} sx={{ 
                  borderBottom: '1px solid #2a2a2a',
                  '&:hover': { backgroundColor: '#222' },
                }}>
                  <TableCell sx={{ color: '#fff', borderBottom: 'none' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 500 }}>
                        {apiKey.key_name}
                      </Typography>
                      {apiKey.key_description && (
                        <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
                          {apiKey.key_description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#fff', borderBottom: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {apiKey.project?.repo_slug || 'Unknown'}
                      </Typography>
                      {apiKey.project?.repo_slug && (
                        <Tooltip title="Open GitHub repository">
                          <IconButton
                            size="small"
                            onClick={() => window.open(`https://github.com/${apiKey.project.repo_slug}`, '_blank')}
                            sx={{ color: '#888' }}
                          >
                            <ExternalLink size={14} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#fff', borderBottom: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        sx={{ 
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          minWidth: '120px',
                        }}
                      >
                        {revealedKeys[apiKey.id] || apiKey.api_key_value_masked}
                      </Typography>
                      <Tooltip title={revealedKeys[apiKey.id] ? "Hide key" : "Reveal key"}>
                        <IconButton
                          size="small"
                          onClick={() => revealedKeys[apiKey.id] ? 
                            handleHideKey(apiKey.id) : 
                            handleRevealKey(apiKey.id)
                          }
                          disabled={isRevealing[apiKey.id]}
                          sx={{ color: '#888' }}
                        >
                          {isRevealing[apiKey.id] ? (
                            <CircularProgress size={16} />
                          ) : revealedKeys[apiKey.id] ? (
                            <EyeOff size={16} />
                          ) : (
                            <Eye size={16} />
                          )}
                        </IconButton>
                      </Tooltip>
                      {revealedKeys[apiKey.id] && (
                        <Tooltip title="Copy to clipboard">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyKey(revealedKeys[apiKey.id])}
                            sx={{ color: '#888' }}
                          >
                            <Copy size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    <Chip
                      label={apiKey.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        backgroundColor: apiKey.is_active ? '#1b5e20' : '#424242',
                        color: apiKey.is_active ? '#4caf50' : '#999',
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#888', borderBottom: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Calendar size={14} />
                      <Typography sx={{ fontSize: '0.875rem' }}>
                        {formatDate(apiKey.last_used_at)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {apiKey.is_active && (
                      <Tooltip title="Deactivate API key">
                        <IconButton
                          size="small"
                          onClick={() => handleDeactivateKey(apiKey.id, apiKey.key_name)}
                          disabled={isDeactivating[apiKey.id]}
                          sx={{ 
                            color: '#ff6b6b',
                            '&:hover': { backgroundColor: '#2d1b1b' },
                          }}
                        >
                          {isDeactivating[apiKey.id] ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PowerOff size={16} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: 4, p: 3, backgroundColor: '#1a1a1a', borderRadius: '8px', border: '1px solid #2a2a2a' }}>
        <Typography sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
          Security Best Practices
        </Typography>
        <Box component="ul" sx={{ color: '#888', pl: 2, m: 0 }}>
          <li>Never share your API keys in public repositories or chat channels</li>
          <li>Regularly review and rotate API keys for better security</li>
          <li>Deactivate any API keys that may have been compromised</li>
          <li>Use environment variables to store API keys in your applications</li>
        </Box>
      </Box>
    </Box>
  );
};

export default APIKeysTab; 